import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Download, FileText, Upload } from "lucide-react";
import api from "../services/api";

// every import / export runs for the logged-in admin's distributor; the backend derives the distributor's brand from it
const withDistributor = (params) => {
  const distributorId = sessionStorage.getItem("distributorId");
  return distributorId ? { distributorId, ...(params || {}) } : params;
};

const panelStyle = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 900,
  background: "#fff",
  borderTop: "3px solid #dc2626",
  boxShadow: "0 -4px 16px rgba(0,0,0,0.15)",
  padding: "10px 16px",
  fontSize: "13px",
};

/**
 * The last import's outcome, kept at the bottom of the page until it is closed or the page is reloaded: the counts and, for
 * the rows not imported, each row number (a PDF's rows are its item lines, named by page / line / bill) with its error.
 */
const ImportResultPanel = ({ result, onClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const errors = result.errors || [];
  const failed = result.fatal || errors.length > 0;
  return createPortal(
    <div style={{ ...panelStyle, borderTopColor: failed ? "#dc2626" : "#16a34a" }} role="status">
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <strong>
          {result.label} import — {result.fileName}
        </strong>
        {result.fatal ? (
          <span style={{ color: "#b91c1c" }}>Not imported: {result.fatal}</span>
        ) : (
          <span>
            {result.rowCount ?? 0} row(s): {result.successCount ?? 0} imported,{" "}
            <span style={{ color: result.failureCount ? "#b91c1c" : undefined }}>{result.failureCount ?? 0} not imported</span>
          </span>
        )}
        <span style={{ color: "#6b7280" }}>{result.at}</span>
        <span style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          {errors.length ? (
            <button type="button" className="secondary" onClick={() => setCollapsed((value) => !value)}>
              {collapsed ? `Show errors (${errors.length})` : "Hide errors"}
            </button>
          ) : null}
          <button type="button" className="secondary" onClick={onClose}>
            Close
          </button>
        </span>
      </div>
      {errors.length && !collapsed ? (
        <div style={{ maxHeight: "32vh", overflowY: "auto", marginTop: "8px" }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: "90px" }}>Row</th>
                <th style={{ width: "140px" }}>Field</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((error, index) => (
                <tr key={`${error.rowNumber}-${index}`}>
                  <td>{error.rowNumber}</td>
                  <td>{error.field || "-"}</td>
                  <td>{error.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

/**
 * Import / export buttons of a list page. {@code pdfImportPath} adds an "Import PDF" button (sales invoices from a PDF of bills,
 * read by the PDF column mapping). The outcome of an import stays in a panel at the bottom of the page.
 */
const ImportExportBar = ({ importPath, exportPath, exportFilename, extraParams, onImported, label, pdfImportPath }) => {
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const runImport = async (path, input, kind) => {
    const file = input.current?.files?.[0];
    if (!file) return;
    setImporting(true);
    const base = { label: `${label || ""}${kind === "pdf" ? " (PDF)" : ""}`.trim(), fileName: file.name, at: new Date().toLocaleString("en-IN") };
    try {
      const data = await api.uploadFile(path, file, withDistributor(extraParams));
      const success = data?.successCount ?? 0;
      const failure = data?.failureCount ?? 0;
      setResult({ ...base, ...data });
      if (failure > 0) toast.warn(`Imported ${success} row(s), ${failure} not imported — see the errors at the bottom of the page`);
      else toast.success(`Imported ${success} row(s) successfully`);
      onImported?.(data);
    } catch (error) {
      setResult({ ...base, fatal: error.message || "Import failed" });
      toast.error(error.message || "Import failed");
    } finally {
      setImporting(false);
      if (input.current) input.current.value = "";
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await api.downloadFile(exportPath, withDistributor(extraParams), exportFilename);
    } catch (error) {
      toast.error(error.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {importPath ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={() => runImport(importPath, fileInputRef, "excel")}
          />
          <button
            type="button"
            className="secondary"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <Upload size={15} />
            {importing ? "Importing..." : `Import ${label || ""}`}
          </button>
        </>
      ) : null}
      {pdfImportPath ? (
        <>
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: "none" }}
            onChange={() => runImport(pdfImportPath, pdfInputRef, "pdf")}
          />
          <button
            type="button"
            className="secondary"
            disabled={importing}
            onClick={() => pdfInputRef.current?.click()}
            title="Invoices from a PDF of bills, read by the PDF column mapping (Import Column Mapping → Sales Invoice (PDF))"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <FileText size={15} />
            {importing ? "Importing..." : "Import PDF"}
          </button>
        </>
      ) : null}
      {exportPath ? (
        <button
          type="button"
          className="secondary"
          disabled={exporting}
          onClick={handleExport}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <Download size={15} />
          {exporting ? "Exporting..." : `Export ${label || ""}`}
        </button>
      ) : null}
      {result ? <ImportResultPanel key={`${result.fileName}-${result.at}`} result={result} onClose={() => setResult(null)} /> : null}
    </div>
  );
};

export default ImportExportBar;
